import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Storefront, Plus, X, Printer, PencilSimple, PaperPlaneTilt, Package, Check, Paperclip, FilePdf, Trash,
} from "@phosphor-icons/react";
import StockPartCombobox from "@/components/StockPartCombobox";
import { fmtDate } from "@/lib/dates";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blankPart = () => ({
  name: "", quantity: 1, stock_code: null, group: null, uom: null, notes: "",
  unit_cost: "", serials: [],
  weight_kg: "", length_cm: "", width_cm: "", height_cm: "",
});

const STATUS_BADGE = {
  draft:     "bg-neutral-200 text-neutral-800",
  sent:      "bg-blue-100 text-blue-800",
  partial:   "bg-amber-100 text-amber-900",
  received:  "bg-emerald-700 text-white",
  cancelled: "bg-red-100 text-red-800 line-through",
};

export default function PurchaseOrdersPage() {
  const { user } = useAuth();
  const canCreate  = ["manager", "supervisor"].includes(user.role);
  const canSend    = ["manager", "supervisor"].includes(user.role);
  const canReceive = user.role === "manager";
  const canCancel  = user.role === "manager";
  const canEmail   = ["manager", "supervisor"].includes(user.role);

  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [attaching, setAttaching] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (search.trim()) params.q = search.trim();
      const [p, s] = await Promise.all([
        api.get("/purchase-orders", { params }),
        api.get("/suppliers"),
      ]);
      setRows(p.data || []);
      setSuppliers(s.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [search]);

  const openPdf = (p) => window.open(`${API_BASE}/purchase-orders/${p.id}/receipt.pdf`, "_blank", "noopener");

  const sendPo = async (p) => {
    if (!window.confirm(`Send ${p.po_no} to ${p.supplier_name}? Marks as SENT (no stock movement yet).`)) return;
    try { await api.post(`/purchase-orders/${p.id}/send`); toast.success(`${p.po_no} sent`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelPo = async (p) => {
    const reason = window.prompt(`Cancel ${p.po_no}? (Optional reason)`, "");
    if (reason === null) return;
    try {
      await api.post(`/purchase-orders/${p.id}/cancel`, null, { params: reason ? { reason } : {} });
      toast.success(`${p.po_no} cancelled`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const emailPo = async (p) => {
    if (!window.confirm(`Email ${p.po_no} to ${p.supplier_email || "supplier"}? (You will be CC'd)`)) return;
    try {
      const res = await api.post(`/purchase-orders/${p.id}/email`);
      toast.success(`Sent to ${res.data.to}${res.data.cc?.length ? ` · CC ${res.data.cc.join(", ")}` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="border-b border-neutral-200 pt-10">
        <div className="flex items-start justify-between px-10 pb-6">
          <div>
            <div className="label-eyebrow">Procurement</div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2 flex items-center gap-3">
              <Storefront size={36} weight="duotone" className="text-blue-700" /> Purchase Orders
            </h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-lg">
              Numbered <span className="font-mono">PO-10001+</span>. Manager/Supervisor drafts + sends → Manager receives (partial receipts allowed) → stock adds on receipt.
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreate(true)} data-testid="new-po-btn"
              className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
              <Plus size={14} weight="bold" className="mr-1" /> New PO
            </Button>
          )}
        </div>
        <div className="px-10 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="Search PO no or supplier…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="h-10 rounded-none border-neutral-300" data-testid="po-search" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="po-status-filter" className="h-10 rounded-none border-neutral-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-10 py-6">
        {loading ? (
          <div className="text-sm text-neutral-500 font-mono">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 p-8 text-center">
            No purchase orders yet.
          </div>
        ) : (
          <div className="border border-neutral-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-300">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">PO No</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Supplier</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Lines</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Received</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Status</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Created</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const ordered = (p.parts || []).reduce((s, x) => s + (x.quantity || 0), 0);
                  const received = (p.parts || []).reduce((s, x) => s + (x.received_qty || 0), 0);
                  return (
                    <tr key={p.id} className="border-b border-neutral-100 last:border-0" data-testid={`po-row-${p.po_no}`}>
                      <td className="px-3 py-2 font-mono font-semibold">{p.po_no}</td>
                      <td className="px-3 py-2">{p.supplier_name}<div className="text-[10px] text-neutral-500">{p.supplier_email || p.supplier_phone || ""}</div></td>
                      <td className="px-3 py-2 text-right font-mono">{p.parts?.length || 0}</td>
                      <td className="px-3 py-2 text-right font-mono">{received}/{ordered}</td>
                      <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_BADGE[p.status] || ""}`}>{p.status}</span></td>
                      <td className="px-3 py-2 text-[11px] font-mono">{fmtDate(p.created_at)}<div className="text-[10px] text-neutral-500">{p.created_by_name}</div></td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openPdf(p)} title="Print / PDF" className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-neutral-900" data-testid={`po-print-${p.po_no}`}><Printer size={13} weight="bold" /></button>
                          {["manager"].includes(user.role) && (
                            <button onClick={() => setAttaching(p)} title={`Attachments (${(p.attachments || []).filter((a) => !a.is_deleted).length})`}
                              className={`h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-neutral-900 relative`}
                              data-testid={`po-attach-${p.po_no}`}>
                              <Paperclip size={13} weight="bold" />
                              {(p.attachments || []).filter((a) => !a.is_deleted).length > 0 && (
                                <span className="absolute -top-1 -right-1 h-3.5 w-3.5 flex items-center justify-center bg-emerald-700 text-white text-[8px] font-bold rounded-full">
                                  {(p.attachments || []).filter((a) => !a.is_deleted).length}
                                </span>
                              )}
                            </button>
                          )}
                          {canEmail && p.supplier_email && (
                            <button onClick={() => emailPo(p)} title={`Email to ${p.supplier_email}`} className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-emerald-700 hover:text-emerald-800" data-testid={`po-email-${p.po_no}`}><PaperPlaneTilt size={13} weight="bold" /></button>
                          )}
                          {canCreate && p.status === "draft" && (
                            <button onClick={() => setEditing(p)} title="Edit" className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-neutral-900" data-testid={`po-edit-${p.po_no}`}><PencilSimple size={13} weight="bold" /></button>
                          )}
                          {canSend && p.status === "draft" && (
                            <button onClick={() => sendPo(p)} className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-blue-700 text-blue-800 hover:bg-blue-700 hover:text-white" data-testid={`po-send-${p.po_no}`}>Send</button>
                          )}
                          {canReceive && ["sent", "partial"].includes(p.status) && (
                            <button onClick={() => setReceiving(p)} className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white" data-testid={`po-receive-${p.po_no}`}>Receive</button>
                          )}
                          {canCancel && ["draft", "sent"].includes(p.status) && (
                            <button onClick={() => cancelPo(p)} title="Cancel" className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600" data-testid={`po-cancel-${p.po_no}`}><X size={13} weight="bold" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showCreate || editing) && (
        <POEditDialog
          editing={editing}
          suppliers={suppliers}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
        />
      )}
      {receiving && (
        <POReceiveDialog
          po={receiving}
          onClose={() => setReceiving(null)}
          onSaved={() => { setReceiving(null); load(); }}
        />
      )}
      {attaching && (
        <POAttachmentsDialog
          po={attaching}
          onClose={() => setAttaching(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function POAttachmentsDialog({ po, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Inline preview state: null | { attachment, url, kind: "pdf"|"image" }
  const [preview, setPreview] = useState(null);
  const fileRef = React.useRef(null);

  // Revoke any object URL when the preview target changes / the dialog closes,
  // so we don't leak megabytes of blob data in memory.
  useEffect(() => {
    return () => { if (preview?.url) URL.revokeObjectURL(preview.url); };
  }, [preview]);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/purchase-orders/${po.id}/attachments`);
      setItems(res.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large — 15 MB max");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/purchase-orders/${po.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Attached ${file.name}`);
      await refresh();
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const previewAttachment = async (a) => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}/attachments/${a.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const ct = (a.content_type || res.data.type || "").toLowerCase();
      const kind = ct.includes("pdf") ? "pdf" : ct.startsWith("image/") ? "image" : "other";
      if (kind === "other") {
        // Fallback: open in a new tab if not previewable inline.
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }
      setPreview({ attachment: a, url, kind });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.original_filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/purchase-orders/${po.id}/attachments/${a.id}`);
      toast.success("Attachment removed");
      if (preview?.attachment?.id === a.id) closePreview();
      await refresh();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtSize = (n) => n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`${preview ? "max-w-5xl" : "max-w-2xl"} rounded-none border-neutral-900 max-h-[92vh] overflow-hidden flex flex-col`} data-testid="po-attach-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            {preview && (
              <button
                onClick={closePreview}
                className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                data-testid="po-attach-back"
              >
                ← Back
              </button>
            )}
            Attachments · {po.po_no}
            {preview && (
              <span className="text-sm text-neutral-500 font-normal font-mono ml-2 truncate">
                / {preview.attachment.original_filename}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {preview ? (
          <div className="flex-1 min-h-[60vh] border border-neutral-300 bg-neutral-50 overflow-auto" data-testid="po-attach-preview">
            {preview.kind === "pdf" ? (
              <iframe
                src={preview.url}
                title={preview.attachment.original_filename}
                className="w-full h-full min-h-[60vh]"
                data-testid="po-attach-preview-pdf"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img
                  src={preview.url}
                  alt={preview.attachment.original_filename}
                  className="max-w-full max-h-[70vh] object-contain"
                  data-testid="po-attach-preview-img"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 overflow-y-auto">
            <div className="text-sm text-neutral-600">
              Attach the supplier invoice photo or PDF received alongside the goods. Max 15 MB per file. PDF, JPG, PNG, WEBP, HEIC accepted.
            </div>
            <div className="border border-dashed border-neutral-400 p-4">
              <input
                ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
                onChange={onPick} disabled={busy} data-testid="po-attach-input"
                className="text-sm"
              />
              {busy && <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 mt-2">Uploading…</div>}
            </div>
            {loading ? (
              <div className="text-sm text-neutral-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-neutral-500 border border-neutral-200 p-4 text-center">
                No attachments yet.
              </div>
            ) : (
              <ul className="border border-neutral-200 divide-y divide-neutral-100">
                {items.map((a) => {
                  const ct = (a.content_type || "").toLowerCase();
                  const isImg = ct.startsWith("image/");
                  const isPdf = ct.includes("pdf");
                  return (
                    <li key={a.id} className="flex items-center justify-between px-3 py-2" data-testid={`po-attach-row-${a.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FilePdf size={16} weight="duotone" className={isPdf ? "text-red-700 shrink-0" : "text-neutral-500 shrink-0"} />
                        <div className="min-w-0">
                          <div className="text-sm truncate">{a.original_filename}</div>
                          <div className="text-[10px] font-mono text-neutral-500">
                            {fmtSize(a.size)} · {fmtDate(a.uploaded_at)} · {a.uploaded_by_name}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => previewAttachment(a)}
                          title={isImg || isPdf ? "Preview" : "Open (external)"}
                          className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                          data-testid={`po-attach-preview-${a.id}`}>
                          {isImg || isPdf ? "Preview" : "Open"}
                        </button>
                        <button onClick={() => remove(a)} title="Delete"
                          className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600"
                          data-testid={`po-attach-del-${a.id}`}>
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          {preview && (
            <a
              href={preview.url}
              download={preview.attachment.original_filename}
              className="h-9 px-3 flex items-center border border-neutral-900 text-xs font-mono uppercase tracking-wider hover:bg-neutral-900 hover:text-white"
              data-testid="po-attach-download"
            >
              Download
            </a>
          )}
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function POEditDialog({ editing, suppliers, onClose, onSaved }) {
  const isEdit = !!editing;
  const [supplierId, setSupplierId] = useState(editing?.supplier_id || "");
  const [expectedAt, setExpectedAt] = useState(editing?.expected_at || "");
  const [items, setItems] = useState(() =>
    (editing?.parts?.length ? editing.parts : [blankPart()]).map((p) => ({
      name: p.name || "", quantity: p.quantity || 1,
      stock_code: p.stock_code || null, group: p.group || null, uom: p.uom || null,
      notes: p.notes || "", unit_cost: p.unit_cost ?? "",
      serials: Array.isArray(p.serials) ? p.serials : [],
      weight_kg: p.weight_kg ?? "", length_cm: p.length_cm ?? "",
      width_cm: p.width_cm ?? "", height_cm: p.height_cm ?? "",
    })),
  );
  const [notes, setNotes] = useState(editing?.notes || "");
  const [busy, setBusy] = useState(false);

  const setItem = (i, key, val) => { const n = items.slice(); n[i] = { ...n[i], [key]: val }; setItems(n); };
  const pickItem = (i, picked) => setItems(items.map((row, idx) => (idx === i ? {
    ...row, name: picked.name || "", stock_code: picked.stock_code || null,
    group: picked.group || null, uom: picked.uom || null,
    unit_cost: row.unit_cost || picked.unit_cost || "",
  } : row)));
  const addRow = () => setItems([...items, blankPart()]);
  const removeRow = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);

  const totalCost = items.reduce((s, p) => s + (Number(p.unit_cost) || 0) * (Number(p.quantity) || 0), 0);

  const submit = async () => {
    if (!supplierId) return toast.error("Pick a supplier");
    const clean = items.filter((p) => (p.name || "").trim() && Number(p.quantity) > 0);
    if (!clean.length) return toast.error("Add at least one line");
    setBusy(true);
    try {
      const payload = {
        supplier_id: supplierId,
        expected_at: expectedAt || null,
        notes,
        parts: clean.map((p) => ({
          name: p.name.trim(),
          quantity: Math.max(1, Number(p.quantity) || 1),
          stock_code: p.stock_code || null,
          group: p.group || null, uom: p.uom || null, notes: p.notes || "",
          unit_cost: p.unit_cost === "" ? null : Number(p.unit_cost),
          serials: (p.serials || []).map((s) => String(s).trim()).filter(Boolean),
          weight_kg: p.weight_kg === "" ? null : Number(p.weight_kg),
          length_cm: p.length_cm === "" ? null : Number(p.length_cm),
          width_cm:  p.width_cm  === "" ? null : Number(p.width_cm),
          height_cm: p.height_cm === "" ? null : Number(p.height_cm),
        })),
      };
      if (isEdit) await api.patch(`/purchase-orders/${editing.id}`, payload);
      else await api.post("/purchase-orders", payload);
      toast.success(isEdit ? "PO updated" : "PO created (draft)");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[92vh] overflow-y-auto" data-testid="po-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? `Edit ${editing.po_no}` : "New Purchase Order"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Supplier *</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="po-supplier" className="h-10 rounded-none border-neutral-400"><SelectValue placeholder="Pick supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Expected delivery</Label>
              <Input value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)}
                placeholder="e.g. 2026-02-15 or Q1 2026" className="h-10 rounded-none border-neutral-400" data-testid="po-expected" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label className="label-eyebrow">Lines *</Label>
              <Button onClick={addRow} size="sm" variant="outline" className="h-7 rounded-none border-neutral-400" data-testid="po-add-line">
                <Plus size={12} weight="bold" className="mr-1" /> Add line
              </Button>
            </div>
            {items.map((p, i) => (
              <div key={i} className="border border-neutral-200 p-2 space-y-2" data-testid={`po-row-${i}`}>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-7"><StockPartCombobox value={p} onChange={(pk) => pickItem(i, pk)} testid={`po-picker-${i}`} /></div>
                  <Input type="number" min={1} value={p.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)}
                    className="col-span-2 h-9 rounded-none border-neutral-400 text-right font-mono" data-testid={`po-qty-${i}`} placeholder="Qty" />
                  <Input type="number" step="0.01" value={p.unit_cost} onChange={(e) => setItem(i, "unit_cost", e.target.value)}
                    className="col-span-2 h-9 rounded-none border-neutral-400 text-right font-mono" data-testid={`po-uc-${i}`} placeholder="Unit cost" />
                  <button type="button" onClick={() => removeRow(i)} disabled={items.length === 1}
                    className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600 disabled:opacity-40" aria-label="Remove"><X size={13} weight="bold" /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center text-sm border-t border-neutral-200 pt-2 font-mono">
            <span className="text-neutral-500 label-eyebrow">Grand Total</span>
            <span className="font-semibold">RM {totalCost.toFixed(2)}</span>
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Notes</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full rounded-none border border-neutral-400 px-2 py-1.5 text-sm focus:outline-none focus:border-neutral-900" data-testid="po-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="po-save" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Create draft")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function POReceiveDialog({ po, onClose, onSaved }) {
  const [lines, setLines] = useState(
    (po.parts || []).map((p) => ({
      ordered: p.quantity, already: p.received_qty || 0,
      remaining: (p.quantity || 0) - (p.received_qty || 0),
      receiving: (p.quantity || 0) - (p.received_qty || 0), // pre-fill full remaining
      serials: "",
    })),
  );
  const [busy, setBusy] = useState(false);

  const setLine = (i, key, val) => { const n = lines.slice(); n[i] = { ...n[i], [key]: val }; setLines(n); };

  const submit = async () => {
    const payload = {
      lines: lines
        .map((l, idx) => ({
          line_index: idx,
          received_qty: Math.max(0, Math.min(Number(l.receiving) || 0, l.remaining)),
          serials: l.serials ? l.serials.split(/[\n,]/).map((s) => s.trim()).filter(Boolean) : [],
        }))
        .filter((l) => l.received_qty > 0),
    };
    if (!payload.lines.length) return toast.error("Enter at least one qty to receive");
    setBusy(true);
    try {
      const res = await api.post(`/purchase-orders/${po.id}/receive`, payload);
      toast.success(`Received — status: ${res.data.status}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900" data-testid="po-receive-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Receive {po.po_no} <Package size={22} weight="duotone" className="inline-block ml-1 text-emerald-700" />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-neutral-500">
            Enter this receipt's incremental qty per line. Fully-received lines lock; the PO auto-flips to <span className="font-mono uppercase">received</span> when every line is complete.
          </div>
          <table className="w-full text-sm border border-neutral-300">
            <thead className="bg-neutral-50 border-b border-neutral-300">
              <tr>
                <th className="text-left px-2 py-1 font-mono text-[10px] uppercase">Part</th>
                <th className="text-right px-2 py-1 font-mono text-[10px] uppercase">Ordered</th>
                <th className="text-right px-2 py-1 font-mono text-[10px] uppercase">Already</th>
                <th className="text-right px-2 py-1 font-mono text-[10px] uppercase">Receiving now</th>
                <th className="text-left px-2 py-1 font-mono text-[10px] uppercase">Serials (comma-sep)</th>
              </tr>
            </thead>
            <tbody>
              {po.parts.map((p, i) => (
                <tr key={i} className="border-b border-neutral-100 last:border-0" data-testid={`po-recv-row-${i}`}>
                  <td className="px-2 py-1"><div className="font-medium">{p.name}</div><div className="text-[10px] text-neutral-500">{p.stock_code || "—"}</div></td>
                  <td className="px-2 py-1 text-right font-mono">{lines[i].ordered}</td>
                  <td className="px-2 py-1 text-right font-mono">{lines[i].already}</td>
                  <td className="px-2 py-1 text-right">
                    <Input type="number" min={0} max={lines[i].remaining} value={lines[i].receiving}
                      onChange={(e) => setLine(i, "receiving", e.target.value)}
                      className="w-20 ml-auto text-right h-8 rounded-none border-neutral-400 font-mono" data-testid={`po-recv-qty-${i}`} />
                    <div className="text-[10px] text-neutral-500">max {lines[i].remaining}</div>
                  </td>
                  <td className="px-2 py-1"><Input value={lines[i].serials} onChange={(e) => setLine(i, "serials", e.target.value)}
                    placeholder="SN-001, SN-002…" className="h-8 rounded-none border-neutral-400 text-xs font-mono" data-testid={`po-recv-serials-${i}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="po-recv-save" className="rounded-none bg-emerald-700 text-white hover:bg-emerald-800">
            <Check size={14} weight="bold" className="mr-1" />
            {busy ? "Receiving…" : "Confirm Receipt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
